// The root error page (#9166 as reference, MIT, t3code; re-implemented behind
// Cleo's names): when the router's root boundary catches an error the person
// sees an always-open report and one button that copies the whole of it —
// app name and version, the page they were on, the time, the stack and up to
// five levels of the cause chain. The path never carries the query string or
// hash, so a pairing token cannot reach the clipboard.
import { Check, Copy } from "lucide-react";
import { useMemo } from "react";

import { APP_DISPLAY_NAME, APP_VERSION } from "../branding";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";
import { Button } from "./ui/button";

/** How deep the cause chain is followed; deeper causes are counted, not printed. */
export const ERROR_REPORT_CAUSE_DEPTH = 5;

export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  return "An unexpected router error occurred.";
}

export function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "No additional error details are available.";
  }
}

/** The page path with its query string and hash dropped — a pairing token lives there. */
export function errorReportPath(location: {
  readonly pathname: string;
  readonly hash?: string;
}): string {
  const hashPath = location.hash?.startsWith("#/") ? location.hash.slice(1).split("?")[0] : null;
  return hashPath ?? location.pathname;
}

export interface ErrorReportInput {
  readonly error: unknown;
  readonly appName: string;
  readonly version: string;
  readonly path: string;
  readonly at: Date;
}

/** The text the Copy button puts on the clipboard: the whole report, nothing to select by hand. */
export function buildErrorReport(input: ErrorReportInput): string {
  const lines: string[] = [
    `${input.appName} ${input.version}`,
    `Page: ${input.path}`,
    `Time: ${input.at.toISOString()}`,
    "",
    errorDetails(input.error),
  ];
  let cause: unknown = input.error instanceof Error ? input.error.cause : undefined;
  let depth = 0;
  while (cause !== undefined && cause !== null) {
    depth += 1;
    if (depth > ERROR_REPORT_CAUSE_DEPTH) {
      lines.push("", `Caused by: (further causes omitted)`);
      break;
    }
    lines.push("", `Caused by (${depth}): ${errorDetails(cause)}`);
    cause = cause instanceof Error ? cause.cause : undefined;
  }
  return lines.join("\n");
}

export function RootErrorPage({
  error,
  reset,
}: {
  readonly error: unknown;
  readonly reset: () => void;
}) {
  const message = errorMessage(error);
  const report = useMemo(
    () =>
      buildErrorReport({
        error,
        appName: APP_DISPLAY_NAME,
        version: APP_VERSION,
        path: typeof window === "undefined" ? "/" : errorReportPath(window.location),
        at: new Date(),
      }),
    [error],
  );
  const { copyToClipboard, isCopied } = useCopyToClipboard({ target: "error report" });

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          Something went wrong.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => reset()}>
            Try again
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            Reload app
          </Button>
          <Button
            size="sm"
            variant="outline"
            aria-label={isCopied ? "Error report copied" : "Copy error report"}
            onClick={() => copyToClipboard(report, undefined)}
          >
            {isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {isCopied ? "Copied" : "Copy error"}
          </Button>
        </div>

        <div className="mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
          <p className="px-3 py-2 text-xs font-medium text-muted-foreground">Error report</p>
          <pre
            data-testid="root-error-report"
            className="max-h-56 overflow-auto border-t border-border/70 bg-background/80 px-3 py-2 text-xs whitespace-pre-wrap text-foreground/85"
          >
            {report}
          </pre>
        </div>
      </section>
    </div>
  );
}
